import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Search, ClipboardList, Clock, Pencil, Trash2, BarChart3, PowerOff } from 'lucide-react';
import AssessmentClient, { AssessmentEntity, AssessmentListResponse } from '../data/api/AssessmentClient';
import Loader from '../../../shared/components/Loader';
import Alert from '../../../shared/components/Alert';
import Modal from '../../../shared/components/Modal';
import AnimatedTabs from '../../../shared/components/AnimatedTabs';
import DateRangePicker from '../../../shared/components/DateRangePicker';
import '../styles/AssessmentStyles.css';

type TabType = 'upcoming' | 'active' | 'closed';
const TABS: TabType[] = ['upcoming', 'active', 'closed'];

const STATUS_CONFIG: Record<string, { bg: string; color: string }> = {
  Upcoming: { bg: '#e0e7ff', color: '#3730a3' },
  Active: { bg: '#dcfce7', color: '#166534' },
  Closed: { bg: '#f1f5f9', color: '#475569' },
};

const AssessmentListPage: React.FC = () => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState<AssessmentEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  
  const [activeTab, setActiveTab] = useState<TabType>(
    () => (sessionStorage.getItem('assessmentListTab') as TabType) || 'upcoming'
  );

  useEffect(() => {
    sessionStorage.setItem('assessmentListTab', activeTab);
  }, [activeTab]);

  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [appliedRange, setAppliedRange] = useState<{ from: string; to: string } | null>(null);

  const handleApply = (from: string, to: string) => {
    setAppliedRange(from && to ? { from, to } : null);
  };

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [deleteAlert, setDeleteAlert] = useState<{ show: boolean; id: string; name: string }>({
    show: false, id: '', name: '',
  });
  const [closeAlert, setCloseAlert] = useState<{ show: boolean; id: string; name: string }>({
    show: false, id: '', name: '',
  });
  const [infoAlert, setInfoAlert] = useState({ show: false, title: '', message: '', type: 'success' as 'success' | 'error' });

  useEffect(() => {
    setPage(1);
    setAssessments([]);
    setHasMore(true);
  }, [activeTab, searchQuery, appliedRange]);

  useEffect(() => {
    const load = async () => {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);

      try {
        const statusParam = activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
        const data: AssessmentListResponse = await AssessmentClient.getAssessments({
          page_number: page,
          limit: 10,
          status: statusParam,
          assessment_name: searchQuery || undefined,
          start_date: appliedRange?.from || undefined,
          end_date: appliedRange?.to || undefined,
        });

        const records = data.records || [];
        setTotalCount(data.total_no_of_records ?? records.length);

        if (page === 1) {
          setAssessments(records);
        } else {
          setAssessments(prev => [...prev, ...records]);
        }

        if (records.length < 10) setHasMore(false);
      } catch (err) {
        console.error('Failed to load assessments:', err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };

    if (hasMore || page === 1) load();
  }, [page, activeTab, searchQuery, appliedRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50 && !loading && !loadingMore && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await AssessmentClient.deleteAssessment(deleteAlert.id);
      setAssessments(prev => prev.filter(a => a.assessment_id !== deleteAlert.id));
      setTotalCount(prev => Math.max(0, prev - 1));
      setInfoAlert({ show: true, title: 'Deleted', message: 'Assessment deleted successfully.', type: 'success' });
    } catch {
      setInfoAlert({ show: true, title: 'Error', message: 'Failed to delete the assessment.', type: 'error' });
    } finally {
      setDeleting(false);
      setDeleteAlert({ show: false, id: '', name: '' });
    }
  }, [deleteAlert.id]);

  const [closing, setClosing] = useState(false);
  const handleCloseConfirm = useCallback(async () => {
    setClosing(true);
    try {
      await AssessmentClient.closeAssessment(closeAlert.id);
      setAssessments(prev => prev.filter(a => a.assessment_id !== closeAlert.id));
      setTotalCount(prev => Math.max(0, prev - 1));
      setInfoAlert({ show: true, title: 'Closed', message: 'Assessment closed successfully.', type: 'success' });
    } catch {
      setInfoAlert({ show: true, title: 'Error', message: 'Failed to close the assessment.', type: 'error' });
    } finally {
      setClosing(false);
      setCloseAlert({ show: false, id: '', name: '' });
    }
  }, [closeAlert.id]);

  const formatDateTime = (raw: string) => {
    if (!raw) return '-';
    const iso = raw.endsWith('Z') ? raw : raw + 'Z';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return raw;
    
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const year = d.getUTCFullYear();

    let hours = d.getUTCHours();
    const minutes = String(d.getUTCMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const strHours = String(hours).padStart(2, '0');

    return `${day}/${month}/${year} ${strHours}:${minutes} ${ampm}`;
  };

  return (
    <motion.div className="learner-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
      <Loader isLoading={loading || deleting} message={deleting ? "Deleting assessment..." : "Loading assessments..."} />

      <Modal open={deleteAlert.show} onClose={() => setDeleteAlert({ show: false, id: '', name: '' })} title="Delete Assessment" width="420px">
        <p>Are you sure you want to delete this assessment? This action cannot be undone.</p>
        {deleteAlert.show && (
          <p style={{ fontWeight: 600, marginTop: 8, color: '#0f172a' }}>{deleteAlert.name}</p>
        )}
        <div className="modal-actions">
          <button className="btn-cancel" onClick={() => setDeleteAlert({ show: false, id: '', name: '' })}>Cancel</button>
          <button className="btn-danger" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </Modal>

      <Modal open={closeAlert.show} onClose={() => setCloseAlert({ show: false, id: '', name: '' })} title="Close Assessment" width="420px">
        <div style={{ padding: '4px 0 16px', color: '#475569', fontSize: '14px', lineHeight: 1.5 }}>
          Are you sure you want to manually close the assessment <strong>{closeAlert.name}</strong>?
          Candidates will no longer be able to start or submit it.
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
          <button className="btn-cancel" onClick={() => setCloseAlert({ show: false, id: '', name: '' })} disabled={closing}>
            Cancel
          </button>
          <button className="btn-danger" style={{ backgroundColor: '#ef4444', borderColor: '#ef4444' }} onClick={handleCloseConfirm} disabled={closing}>
            {closing ? 'Closing...' : 'Close Assessment'}
          </button>
        </div>
      </Modal>

      <Alert
        show={infoAlert.show}
        title={infoAlert.title}
        message={infoAlert.message}
        type={infoAlert.type}
        onClose={() => setInfoAlert(prev => ({ ...prev, show: false }))}
        primaryButtonText="OK"
        onPrimaryClick={() => setInfoAlert(prev => ({ ...prev, show: false }))}
      />

      {/* Hero */}
      <div className="lp-hero" style={{ flexDirection: 'column', gap: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
          <div className="lp-hero-left">
            <h1>Assessments</h1>
            <span className="lp-hero-chip">Manage and schedule skill assessments</span>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px 20px', gap: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ width: 40, height: 40, borderRadius: '10px', background: '#e0e7ff', color: '#3730a3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ClipboardList size={20} strokeWidth={2.5} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1rem', color: '#64748b', fontWeight: 600 }}>Total Assessments:</span>
              <span style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{totalCount}</span>
            </div>
          </div>

          <AnimatedTabs
            variant="line"
            size="md"
            tabs={TABS.map(t => ({
              label: t.charAt(0).toUpperCase() + t.slice(1),
              count: activeTab === t ? totalCount : undefined,
            }))}
            activeTab={activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
            onChange={label => setActiveTab(label.toLowerCase() as TabType)}
          />

          <div className="lp-hero-right" style={{ gap: '12px', display: 'flex', alignItems: 'center' }}>
            <button className="btn-primary" onClick={() => navigate('/dashboard/assessments/create')}>
              <Plus size={18} />
              Create Assessment
            </button>
          </div>
        </div>
      </div>

      {/* Table Panel */}
      <div className="lp-table-panel" style={{ marginTop: '16px' }}>
        <div className="lp-table-panel-head" style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexWrap: 'wrap', gap: '16px' }}>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>
            {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Assessments
          </h2>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="lp-search" style={{ height: 40, width: 260, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', position: 'relative' }}>
              <Search size={16} color="#94a3b8" />
              <input
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ paddingRight: searchQuery ? '30px' : '10px' }}
              />
              {searchQuery && (
                <button
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: '#f1f5f9', cursor: 'pointer', color: '#64748b', width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                  onClick={() => setSearchQuery('')}
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              )}
            </div>

            <DateRangePicker
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFromChange={setDateFrom}
              onDateToChange={setDateTo}
              onApply={handleApply}
            />
          </div>
        </div>

        <div className="lp-table-scroll-area" onScroll={handleScroll} style={{ maxHeight: '600px' }}>
          {assessments.length === 0 && !loading ? (
            <div className="is-empty">
              <ClipboardList size={48} color="#cbd5e1" style={{ margin: '0 auto' }} />
              <h3>No {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Assessments</h3>
              <p>There are no assessments matching your criteria.</p>
            </div>
          ) : (
            <>
              <table className="learner-table">
                <thead>
                  <tr>
                    <th>Assessment Name</th>
                    <th>Start Time</th>
                    <th>End Time</th>
                    <th>Duration (min)</th>
                    <th>Code</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assessments.map(item => {
                    const sc = STATUS_CONFIG[item.status || 'Upcoming'] || STATUS_CONFIG.Upcoming;
                    return (
                      <tr key={item.assessment_id}>

                        <td>
                          <strong style={{ color: '#0f172a' }}>
                            {item.assessment_name?.length > 25 ? item.assessment_name.slice(0, 25) + '...' : item.assessment_name}
                          </strong>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                            <Clock size={14} color="#94a3b8" />
                            {formatDateTime(item.assessment_start_time)}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                            <Clock size={14} color="#94a3b8" />
                            {formatDateTime(item.assessment_end_time)}
                          </div>
                        </td>
                        <td style={{ color: '#475569' }}>{item.assessment_duration}</td>
                        <td>
                          <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, background: '#f1f5f9', color: '#334155', fontFamily: 'monospace' }}>
                            {item.assessment_otp || '-'}
                          </span>
                        </td>
                        <td>
                          <div className="actions-row">
                            {item.status !== 'Upcoming' && (
                              <button
                                className="action-btn view"
                                title="View Results"
                                onClick={() => navigate(`/dashboard/assessments/results/${item.assessment_id}`)}
                              >
                                <BarChart3 size={18} />
                              </button>
                            )}
                            {item.status !== 'Closed' && (
                              <button
                                className="action-btn edit"
                                title="Edit"
                                onClick={() => navigate(`/dashboard/assessments/edit/${item.assessment_id}`)}
                              >
                                <Pencil size={18} />
                              </button>
                            )}
                            {item.status === 'Active' && (
                              <button
                                className="action-btn delete"
                                title="Close Assessment"
                                style={{ color: '#ea580c', backgroundColor: '#fff7ed' }}
                                onClick={() => setCloseAlert({ show: true, id: item.assessment_id || '', name: item.assessment_name })}
                              >
                                <PowerOff size={18} />
                              </button>
                            )}
                            {item.status === 'Upcoming' && (
                              <button
                                className="action-btn delete"
                                title="Delete"
                                onClick={() => setDeleteAlert({ show: true, id: item.assessment_id || '', name: item.assessment_name })}
                              >
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {loadingMore && (
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <span style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading more assessments...</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default AssessmentListPage;
